import { Box, Typography, Chip, Accordion, AccordionSummary, AccordionDetails } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import WorkIcon from "@mui/icons-material/Work";
import PersonIcon from "@mui/icons-material/Person";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import { ExpandableSection } from "./ExpandableSection";
import { useState, useEffect } from "react";

type ServiceData = {
    key: string;
    name: string;
    color: string;
    possibleSessions: number;
    weeklyHours: number;
    users: Array<{ name: string; totalHours: number }>;
};

type HoursPerServiceProps = {
    services: Array<ServiceData>;
    expanded: boolean;
    onChange: (expanded: boolean) => void;
};

export const HoursPerService = ({ services, expanded, onChange }: HoursPerServiceProps) => {
    const theme = useTheme();

    // Initialize all services as expanded by default
    const [serviceExpanded, setServiceExpanded] = useState<Record<string, boolean>>(() => {
        const initialState: Record<string, boolean> = {};
        services.forEach(service => {
            initialState[service.key] = true;
        });
        return initialState;
    });

    // Update state when services change
    useEffect(() => {
        setServiceExpanded(prev => {
            const updatedState = { ...prev };
            services.forEach(service => {
                if (updatedState[service.key] === undefined) {
                    updatedState[service.key] = true; // New services start expanded
                }
            });
            return updatedState;
        });
    }, [services]);

    const handleServiceToggle = (serviceKey: string) => {
        setServiceExpanded(prev => ({
            ...prev,
            [serviceKey]: !prev[serviceKey]
        }));
    };

    return (
        <ExpandableSection
            title="Por Serviço"
            icon={<WorkIcon sx={{ color: theme.palette.secondary.main, fontSize: 20 }} />}
            expanded={expanded}
            onChange={onChange}
        >
            <Box display="flex" flexDirection="column" gap={1}>
                {services.map((item) => (
                    <Accordion
                        key={item.key}
                        expanded={serviceExpanded[item.key] ?? true}
                        onChange={() => handleServiceToggle(item.key)}
                        disableGutters
                        square
                        sx={{
                            bgcolor: "transparent",
                            color: "inherit",
                            "&:before": { display: "none" },
                            borderRadius: 2,
                            border: `1px solid ${theme.palette.divider}`,
                            "&:not(:last-child)": {
                                mb: 1,
                            },
                        }}
                        TransitionProps={{ timeout: 200 }}
                    >
                        <AccordionSummary
                            expandIcon={<ExpandMoreIcon sx={{ color: theme.palette.text.secondary }} />}
                            sx={{
                                px: 2,
                                py: 1.5,
                                "&:hover": {
                                    bgcolor: theme.palette.action.hover,
                                },
                                borderRadius: 2,
                                minHeight: "auto",
                            }}
                        >
                            <Box display="flex" alignItems="center" justifyContent="space-between" width="100%" mr={2}>
                                <Box display="flex" alignItems="center" gap={1}>
                                    <Box
                                        sx={{
                                            width: 12,
                                            height: 12,
                                            borderRadius: "50%",
                                            bgcolor: item.color,
                                        }}
                                    />
                                    <Typography
                                        variant="subtitle2"
                                        sx={{
                                            fontWeight: 600,
                                            color: item.color,
                                        }}
                                    >
                                        {item.name}
                                    </Typography>
                                </Box>
                                <Chip
                                    icon={<CalendarMonthIcon sx={{ fontSize: 16 }} />}
                                    label={item.possibleSessions}
                                    size="small"
                                    variant="outlined"
                                    sx={{
                                        borderColor: item.color,
                                        color: item.color,
                                        "& .MuiChip-icon": {
                                            color: item.color,
                                            marginLeft: "6px",
                                            marginRight: "-4px",
                                        },
                                    }}
                                />
                            </Box>
                        </AccordionSummary>

                        <AccordionDetails sx={{ px: 2, pb: 2 }}>
                            <Box display="flex" flexDirection="column" gap={1.5}>
                                {item.users.map((u) => (
                                    <Box
                                        key={`${item.key}-${u.name}`}
                                        display="flex"
                                        alignItems="center"
                                        justifyContent="space-between"
                                    >
                                        <Box display="flex" alignItems="center" gap={1}>
                                            <PersonIcon sx={{ fontSize: 14, color: theme.palette.text.secondary }} />
                                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                {u.name}
                                            </Typography>
                                        </Box>
                                        <Typography
                                            variant="body2"
                                            sx={{ fontWeight: 600, color: theme.palette.primary.main }}
                                        >
                                            {u.totalHours}h
                                        </Typography>
                                    </Box>
                                ))}
                            </Box>

                            <Box sx={{ mt: 2, pt: 1.5, borderTop: `1px solid ${theme.palette.divider}` }}>
                                <Typography variant="caption" color="text.secondary">
                                    Total semanal: {item.weeklyHours}h
                                </Typography>
                            </Box>
                        </AccordionDetails>
                    </Accordion>
                ))}
            </Box>
        </ExpandableSection>
    );
};
